import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextInput,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import {
  type ICreateFranchiseRequest,
  ValidationConstraints
} from "@dealer-center/ddc-pages-franchise-management-interface";
import { useCreateFranchise } from "@dealer-center/ddc-pages-franchise-management-fetch";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const StyledTextInput = styled(TextInput)`
  &.invalid-input {
    label.MuiInputLabel-root {
      color: #d41e27;
    }
    .MuiInputBase-root.Mui-focused fieldset {
      box-shadow: none;
      border: 1px solid #d41e27;
    }
  }
  & label.MuiInputLabel-root {
    color: black;
  }
`;

const StyledAlert = styled(Alert)`
  &.MuiAlert-root {
    &.MuiAlert-outlined {
      &.MuiAlert-colorInfo {
        color: #856404;
        background-color: #fffce5;
        border: 1px solid #ffeaa7;
      }
    }
  }
`;

export const AddFranchise = () => {
  const navigate = useNavigate();
  const toast = useToaster();
  const [franchiseName, setFranchiseName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [franchiseNameError, setFranchiseNameError] = useState(false);
  const [abbreviationError, setAbbreviationError] = useState(false);
  const [franchiseNameTouched, setFranchiseNameTouched] = useState(false);
  const [abbreviationTouched, setAbbreviationTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: createFranchise } = useCreateFranchise(
    data => {
      toast.toastSuccess("Success", "Franchise created");
      navigate("/franchiseManagement");
    },
    error => {
      if (
        error?.message?.includes("400") ||
        error?.message?.toLowerCase().includes("already exists")
      ) {
        toast.toastError("Franchise name already exists");
      } else {
        toast.toastError("Error creating franchise");
      }
      setIsSubmitting(false);
    }
  );

  const validateFranchiseName = (name: string): boolean => {
    const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
    return !(
      name.trim() === "" ||
      name.trim().length > 50 ||
      !alphanumericRegex.test(name.trim())
    );
  };

  const validateAbbreviation = (abbr: string): boolean => {
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    return !(
      abbr.trim() === "" ||
      abbr.length > 2 ||
      !alphanumericRegex.test(abbr.trim())
    );
  };

  const handleAddFranchise = () => {
    setSubmitAttempted(true);

    // Validate all fields
    const isNameValid = validateFranchiseName(franchiseName);
    const isAbbreviationValid = validateAbbreviation(abbreviation);

    setFranchiseNameError(!isNameValid);
    setAbbreviationError(!isAbbreviationValid);

    if (isNameValid && isAbbreviationValid) {
      setIsSubmitting(true);

      const franchiseData: ICreateFranchiseRequest = {
        name: franchiseName.trim(),
        abbreviation: abbreviation.toUpperCase().trim(),
        userId: "", // Will be populated by backend from JWT
        actingAsUserId: "" // Will be populated by backend from JWT
      };

      createFranchise(franchiseData);
    }
  };

  const handleCancel = () => {
    navigate("/franchiseManagement");
  };

  const handleFranchiseNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFranchiseName(value);

    // Always validate if length exceeds 50 characters for immediate feedback
    // Also validate if field has been touched or submit attempted
    if (value.trim().length > 50 || franchiseNameTouched || submitAttempted) {
      setFranchiseNameError(!validateFranchiseName(value));
    }
  };

  const handleFranchiseNameBlur = () => {
    setFranchiseNameTouched(true);
    setFranchiseNameError(!validateFranchiseName(franchiseName));
  };

  const handleAbbreviationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAbbreviation(value);
    if (value.length > 2 && !abbreviationTouched) {
      setAbbreviationTouched(true);
    }
    if (abbreviationTouched || submitAttempted || value.length > 2) {
      setAbbreviationError(!validateAbbreviation(value));
    }
  };

  const handleAbbreviationBlur = () => {
    setAbbreviationTouched(true);
    setAbbreviationError(!validateAbbreviation(abbreviation));
  };

  const addFranchiseCardHeader = (
    <CardHeader
      title={
        <Typography sx={{ fontWeight: "bold", marginBottom: 2 }} variant="h4">
          Create New Franchise
        </Typography>
      }
    />
  );

  const disableAddFranchiseButton =
    franchiseName.trim() === "" ||
    abbreviation.trim() === "" ||
    franchiseNameError ||
    abbreviationError ||
    isSubmitting;

  const addFranchiseCardContent = (
    <CardContent>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextInput
            className={franchiseNameError ? "invalid-input" : ""}
            errorMessage={
              franchiseNameError
                ? !franchiseName.trim()
                  ? ValidationConstraints.FRANCHISE_CREATE.NAME_NOT_EMPTY
                  : franchiseName.trim().length > 50
                    ? ValidationConstraints.FRANCHISE_CREATE.NAME_MAX_LENGTH
                    : ValidationConstraints.FRANCHISE_CREATE
                        .NAME_ALPHANUMERIC_ONLY
                : ""
            }
            hasError={franchiseNameError}
            id="PagesFranchiseManagementUiAddFranchiseNameTextInput"
            label="Franchise Name *"
            name="PagesFranchiseManagementUiAddFranchiseNameTextInput"
            placeholder="e.g. Toyota"
            value={franchiseName}
            onBlur={handleFranchiseNameBlur}
            onChange={handleFranchiseNameChange}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextInput
            className={abbreviationError ? "invalid-input" : ""}
            errorMessage={
              abbreviationError
                ? !abbreviation.trim()
                  ? ValidationConstraints.FRANCHISE_CREATE
                      .ABBREVIATION_NOT_EMPTY
                  : abbreviation.length > 2
                    ? ValidationConstraints.FRANCHISE_CREATE
                        .ABBREVIATION_MAX_LENGTH
                    : ValidationConstraints.FRANCHISE_CREATE
                        .ABBREVIATION_ALPHANUMERIC_ONLY
                : ""
            }
            hasError={abbreviationError}
            id="PagesFranchiseManagementUiAddFranchiseAbbreviationTextInput"
            label="Abbreviation *"
            name="PagesFranchiseManagementUiAddFranchiseAbbreviationTextInput"
            placeholder="e.g. TO"
            value={abbreviation}
            onBlur={handleAbbreviationBlur}
            onChange={handleAbbreviationChange}
          />
          <Typography
            sx={{ color: "text.secondary", mt: 0.5 }}
            variant="body-sm"
          >
            Will be automatically converted to uppercase
          </Typography>
        </Grid>
      </Grid>

      <StyledAlert
        role="alert"
        sx={{
          "& .MuiAlert-icon": {
            color: "#856404",
            alignSelf: "flex-start",
            marginTop: "6px"
          },
          alignItems: "flex-start"
        }}
        type="info"
      >
        <Typography sx={{ color: "#856404" }} variant="body-lg">
          <strong>Important Notice</strong>
          <br />
          Only one franchise can be edited or created at a time. All changes are
          automatically logged for audit purposes.
        </Typography>
      </StyledAlert>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          mt: 3,
          pt: 3
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-end"
        }}
      >
        <Button
          analyticsConfiguration={{
            eventName: "PagesFranchiseManagementUi Cancel"
          }}
          buttonStyle="secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "PagesFranchiseManagementUi Create Franchise"
          }}
          buttonStyle="primary"
          disabled={disableAddFranchiseButton}
          isLoading={isSubmitting}
          onClick={handleAddFranchise}
        >
          {isSubmitting ? "Creating..." : "Create Franchise"}
        </Button>
      </Box>
    </CardContent>
  );

  return (
    <Card content={addFranchiseCardContent} header={addFranchiseCardHeader} />
  );
};
